import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil } from "@ews-admin/global-design-system";
import { licencePricingApi } from "../../services/licencePricing.api";
import { featureApi } from "../../services/feature.api";
import { LicencePricing } from "../../types/licencePricing.definition";
import { DoctorLicencePlanType } from "../../types/enums";
import { FeatureOption } from "../../types/feature.definition";
import { CURRENCY_SUFFIX } from "../../constants";
import { formatMoney } from "../../utils/money-utils";
import { getFeatureLabel } from "../../utils/feature-label-utils";
import {
  PlanEditorPanel,
  PlanEditorValues,
} from "../../components/molecules/PlanEditorPanel/PlanEditorPanel";
import { stringToFeatures } from "../../components/atoms/FeatureListEditor";
import { LicenceBillingDiscountConfig } from "./LicenceBillingDiscountConfig";

const PLAN_META: Record<
  DoctorLicencePlanType,
  { accentBg: string; badge: string; textOnAccent: string; label: string }
> = {
  [DoctorLicencePlanType.BASIC]: {
    accentBg: "bg-slate-500",
    badge: "bg-slate-100 text-slate-600",
    textOnAccent: "text-white",
    label: "Basic",
  },
  [DoctorLicencePlanType.STANDARD]: {
    accentBg: "bg-primary-600",
    badge: "bg-primary-50 text-primary-700",
    textOnAccent: "text-white",
    label: "Standard",
  },
  [DoctorLicencePlanType.PREMIUM]: {
    accentBg: "bg-amber-500",
    badge: "bg-amber-50 text-amber-700",
    textOnAccent: "text-white",
    label: "Premium",
  },
};

const PLAN_FIELDS = (t: (k: string) => string) => [
  {
    key: "basePrice",
    label: t("admin.pricing.table.basePrice"),
    note: t("admin.pricing.basePriceNote"),
    suffix: CURRENCY_SUFFIX,
    min: 0,
    step: 1000,
  },
  {
    key: "pricePerDoctor",
    label: t("admin.pricing.table.pricePerDoctor"),
    note: t("admin.pricing.doctorPriceNote"),
    suffix: CURRENCY_SUFFIX,
    min: 1,
    step: 1000,
  },
  {
    key: "maxSecretaries",
    label: t("admin.pricing.table.maxSecretaries"),
    note: t("admin.pricing.maxSecretariesNote"),
    min: 0,
    step: 1,
  },
];

interface Props {
  embedded?: boolean;
}

export const LicencePricingManagement: React.FC<Props> = ({
  embedded = false,
}) => {
  const { t, i18n } = useTranslation();
  const [pricings, setPricings] = useState<LicencePricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [editingPlan, setEditingPlan] = useState<LicencePricing | null>(null);
  const [saving, setSaving] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState<FeatureOption[]>(
    []
  );

  useEffect(() => {
    licencePricingApi
      .getAll()
      .then(setPricings)
      .catch(() => setErrorMsg(t("admin.pricing.errorLoading")))
      .finally(() => setLoading(false));
  }, [t]);

  // MED-1558: feature labels in the UI language, refetched when it changes.
  useEffect(() => {
    featureApi
      .getAll(i18n.language)
      .then(fs =>
        setAvailableFeatures(fs.map(f => ({ code: f.code, label: f.label })))
      );
  }, [i18n.language]);

  const openEditor = (p: LicencePricing) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setEditingPlan(p);
  };

  const handleSave = async (values: PlanEditorValues) => {
    if (!editingPlan) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      const updated = await licencePricingApi.update(editingPlan.id, {
        licenceType: editingPlan.licenceType,
        basePrice: Number(values.basePrice),
        pricePerDoctor: Number(values.pricePerDoctor),
        maxSecretaries: Number(values.maxSecretaries),
        features: values.features,
      });
      setPricings(prev =>
        prev.map(p => (p.id === editingPlan.id ? updated : p))
      );
      setEditingPlan(null);
      setSuccessMsg(t("admin.pricing.updateSuccess"));
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch {
      setErrorMsg(t("admin.pricing.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const editorValues: PlanEditorValues | null = editingPlan
    ? {
        basePrice: editingPlan.basePrice,
        pricePerDoctor: editingPlan.pricePerDoctor,
        maxSecretaries: editingPlan.maxSecretaries,
        features: editingPlan.features,
      }
    : null;

  return (
    <div className={embedded ? "" : "page-container"}>
      {!embedded && (
        <div className="page-header">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("admin.pricing.title")}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t("admin.pricing.subtitle")}
            </p>
          </div>
        </div>
      )}

      {successMsg && (
        <div className="p-3 mb-6 text-green-700 bg-green-50 rounded-lg border border-green-200 text-sm">
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="p-3 mb-6 text-red-700 bg-red-50 rounded-lg border border-red-200 text-sm">
          {errorMsg}
        </div>
      )}

      <LicenceBillingDiscountConfig />

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 rounded-full border-b-2 border-primary-600 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {pricings.map(p => {
            const meta =
              PLAN_META[p.licenceType] ??
              PLAN_META[DoctorLicencePlanType.BASIC];
            const features = stringToFeatures(p.features);

            return (
              <div
                key={p.id}
                className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
              >
                <div className={`h-1.5 ${meta.accentBg}`} />

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <div>
                    <span
                      className={`inline-block text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mb-1 ${meta.badge}`}
                    >
                      {t("admin.pricing.plan")}
                    </span>
                    <h3 className="text-base font-bold text-gray-900">
                      {meta.label}
                    </h3>
                  </div>
                  <button
                    onClick={() => openEditor(p)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    {t("common.edit")}
                  </button>
                </div>

                <div className="px-6 py-5 flex-1 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      {t("admin.pricing.table.basePrice")}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatMoney(p.basePrice)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t("admin.pricing.basePriceNote")}
                    </p>
                  </div>

                  <div className="border-t border-gray-100" />

                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      {t("admin.pricing.table.pricePerDoctor")}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatMoney(p.pricePerDoctor)}
                      <span className="text-sm font-normal text-gray-400 ml-1">
                        / {t("admin.pricing.perDoctor")}
                      </span>
                    </p>
                  </div>

                  <div className="border-t border-gray-100" />

                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      {t("admin.pricing.table.maxSecretaries")}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {p.maxSecretaries}
                      <span className="text-sm font-normal text-gray-400 ml-1">
                        {/* MED-1543: read "1 secrétaires max". */}
                        {t("admin.pricing.secretaries", {
                          count: p.maxSecretaries,
                        })}
                      </span>
                    </p>
                  </div>

                  {features.length > 0 && (
                    <>
                      <div className="border-t border-gray-100" />
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                          {t("admin.planEditor.sectionFeatures")}
                        </p>
                        <ul className="space-y-1">
                          {features.map((code, i) => {
                            const featureLabel = getFeatureLabel(
                              code,
                              availableFeatures,
                              t
                            );
                            return (
                              <li
                                key={i}
                                className="flex items-center gap-2 text-sm text-gray-700"
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.accentBg}`}
                                />
                                {featureLabel}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingPlan && editorValues && (
        <PlanEditorPanel
          open={!!editingPlan}
          planMeta={
            PLAN_META[editingPlan.licenceType] ??
            PLAN_META[DoctorLicencePlanType.BASIC]
          }
          planLabel={
            (
              PLAN_META[editingPlan.licenceType] ??
              PLAN_META[DoctorLicencePlanType.BASIC]
            ).label
          }
          fields={PLAN_FIELDS(t)}
          values={editorValues}
          availableFeatures={availableFeatures}
          saving={saving}
          onSave={handleSave}
          onClose={() => setEditingPlan(null)}
          previewConfig={{
            primaryPriceKey: "basePrice",
            secondaryPriceKey: "pricePerDoctor",
            secondaryPriceSuffix: `/ ${t("admin.pricing.perDoctor")}`,
          }}
        />
      )}
    </div>
  );
};
